import moment from "moment";

import {
    get_counterpart,
    get_counterpart_month_docs,
    get_counterpart_monthly_history,
    resolve_counterpart_by_alias,
} from "@/api/carteraAnalista";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";

// Drill-down de segundo nivel: el modal de análisis de Ventas abre este
// drawer cuando el usuario hace click sobre un cliente del ranking. Módulo
// independiente para que futuros tabs (Gastos: proveedor del mes; Costos:
// proveedor del mes) clonen el patrón sin disentangle de salesAnalysis.

// `monthsBack` se sustrae al mes clickeado para definir el rango histórico.
// El selector vive en el header del drawer y mueve sólo la card de Historial
// + KPIs derivadas del rango (Total del último año). La tabla y los KPIs del
// mes (Total del mes, Documentos del mes) quedan fijos al monthIso.
export const RANGE_OPTIONS = [
    { value: "last_year", label: "Último año", monthsBack: 11 },
    { value: "last_6_months", label: "Últimos 6 meses", monthsBack: 5 },
    { value: "last_2_years", label: "Últimos 2 años", monthsBack: 23 },
];

const DEFAULT_RANGE = "last_year";

// Helpers de rango — encapsulan la conversión monthIso + range → (from, to).
// Se exportan para que los specs puedan testear sin instanciar el store.
export function computeRangeBoundaries(monthIso, rangeValue) {
    const option = RANGE_OPTIONS.find((o) => o.value === rangeValue) ?? RANGE_OPTIONS[0];
    const endOfMonth = moment(monthIso, "YYYY-MM").endOf("month");
    const startOfRange = moment(monthIso, "YYYY-MM")
        .subtract(option.monthsBack, "months")
        .startOf("month");
    return {
        from: startOfRange.format("YYYY-MM-DD"),
        to: endOfMonth.format("YYYY-MM-DD"),
    };
}

export function computeMonthBoundaries(monthIso) {
    const start = moment(monthIso, "YYYY-MM").startOf("month");
    const end = moment(monthIso, "YYYY-MM").endOf("month");
    return {
        from: start.format("YYYY-MM-DD"),
        to: end.format("YYYY-MM-DD"),
    };
}

const state = () => ({
    /** Abierto/cerrado controla la visibilidad del drawer secundario. */
    isOpen: false,
    /** YYYY-MM del mes clickeado en el ranking — define el alcance de docs. */
    monthIso: null,
    /** "C" (Cliente) o "P" (Proveedor). */
    documentType: null,
    /** obligation_name del counterpart — necesario para la API. */
    obligationName: "Otros",
    ciId: null,
    /** Counterpart cargado vía `get_counterpart(single=true)`. */
    counterpart: null,
    /**
     * Documentos accruados al mes clickeado, con `amount` prorrateado al
     * período (no el monto facturado real — el original queda en
     * `original_amount` por si la UI lo necesita).
     */
    documents: [],
    /**
     * Suma prorrateada de los documentos del mes (= valor del ranking).
     * Se sirve aparte porque algunos docs aparecen en varios meses con
     * porciones distintas y `documents.reduce(amount)` ya da el total
     * correcto — esta key sólo evita el redondeo per-row.
     */
    totalMonth: 0,
    /** Serie mensual `[{ month_iso, amount, sales_total }]` del rango. */
    monthlyHistory: [],
    /** Rango actual del selector (afecta sólo chart + KPI año). */
    rangePeriod: DEFAULT_RANGE,
    isLoading: false,
    error: null,
    /** Filtro de búsqueda libre sobre número de documento (case-insensitive). */
    search: "",
});

const getters = {
    /** Documentos filtrados por `search` (matching contra doc_number). */
    filteredDocuments: (state) => {
        const q = state.search.trim().toLowerCase();
        if (!q) return state.documents;
        return state.documents.filter((doc) => {
            const num = String(doc.doc_number ?? "").toLowerCase();
            return num.includes(q);
        });
    },
    /** Suma de montos del histórico del rango (KPI "Total del último año"). */
    totalRangeAmount: (state) =>
        state.monthlyHistory.reduce((acc, row) => acc + (row.amount || 0), 0),
    /** Cantidad de documentos del mes (KPI "Documentos del mes"). */
    documentsOfMonthCount: (state) => state.documents.length,
    /**
     * Fecha aproximada del primer documento del counterpart, derivada del
     * primer mes con monto > 0 en la serie. Es una aproximación — la
     * granularidad es mensual. Si necesitamos día exacto, agregar otro
     * endpoint o derivar del documento más antiguo retornado.
     */
    firstActiveMonth: (state) => {
        const found = state.monthlyHistory.find((row) => (row.amount || 0) > 0);
        return found ? found.month_iso : null;
    },
    /** Label legible del rango actual (para el chip del selector). */
    rangeLabel: (state) => {
        const opt = RANGE_OPTIONS.find((o) => o.value === state.rangePeriod);
        return opt ? opt.label : "";
    },
};

const mutations = {
    OPEN(state, { ciId, monthIso, documentType, obligationName }) {
        state.isOpen = true;
        state.ciId = ciId;
        state.monthIso = monthIso;
        state.documentType = documentType;
        state.obligationName = obligationName || "Otros";
        state.rangePeriod = DEFAULT_RANGE;
        state.search = "";
        // Limpieza explícita — el caller espera estado fresco aún si reabre
        // sobre el mismo cliente. No usamos RESET acá porque queremos
        // conservar isOpen=true e los identificadores que acabamos de setear.
        state.counterpart = null;
        state.documents = [];
        state.totalMonth = 0;
        state.monthlyHistory = [];
        state.error = null;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_COUNTERPART(state, counterpart) {
        state.counterpart = counterpart;
    },
    SET_DOCUMENTS(state, documents) {
        state.documents = documents;
    },
    SET_TOTAL_MONTH(state, value) {
        state.totalMonth = value;
    },
    SET_MONTHLY_HISTORY(state, series) {
        state.monthlyHistory = series;
    },
    SET_RANGE_PERIOD(state, value) {
        state.rangePeriod = value;
    },
    SET_SEARCH(state, value) {
        state.search = value;
    },
    RESET(state) {
        state.isOpen = false;
        state.ciId = null;
        state.monthIso = null;
        state.documentType = null;
        state.obligationName = "Otros";
        state.counterpart = null;
        state.documents = [];
        state.totalMonth = 0;
        state.monthlyHistory = [];
        state.rangePeriod = DEFAULT_RANGE;
        state.isLoading = false;
        state.error = null;
        state.search = "";
    },
};

const actions = {
    /**
     * Abre el drawer y carga las tres fuentes en paralelo:
     *  - counterpart (info general)
     *  - documents del mes (tabla)
     *  - monthly history (chart + KPI rango)
     *
     * Las tres llamadas son independientes — un fallo no debe abortar las
     * otras, así que las envolvemos en `Promise.allSettled` y reportamos
     * error sólo si las tres fallaron (criterio: el drawer es inútil sin
     * counterpart Y sin documents; el chart degrada bien sin history).
     */
    async openClientDetail({ commit }, { ciId, alias, documentType, monthIso, obligationName }) {
        commit("OPEN", { ciId: ciId || null, monthIso, documentType, obligationName });
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        // El clients_ranking del salesAnalysis devuelve sólo `name` (alias) —
        // resolvemos a ci_id acá si no vino explícito. Una vez resuelto, lo
        // guardamos en el estado para que changeRangePeriod/reloadDocuments
        // puedan refetchear sin re-resolver.
        let resolvedCiId = ciId;
        if (!resolvedCiId && alias) {
            try {
                const { data: resolved } = await resolve_counterpart_by_alias(alias, documentType);
                resolvedCiId = resolved?.ci_id ?? null;
                commit("OPEN", {
                    ciId: resolvedCiId,
                    monthIso,
                    documentType,
                    obligationName,
                });
            } catch (error) {
                consoleHandlerError({ error, prefix: "[clientDetail] resolve", silent: true });
            }
        }
        if (!resolvedCiId) {
            commit("SET_ERROR", "No se pudo identificar el cliente.");
            commit("SET_LOADING", false);
            return;
        }

        const rangeBounds = computeRangeBoundaries(monthIso, DEFAULT_RANGE);
        const obl = obligationName || "Otros";

        // get_counterpart sigue con semántica emitted_at + obligation Otros —
        // es un endpoint compartido por toda la app y cambiarlo afectaría a
        // otros consumidores. Lo usamos solo para metadata (alias, rut, tipo,
        // clasificación, cuentas, atributos) que no depende del mes.
        //
        // Pasamos from_day/to_day = null para que el backend caiga en su
        // default de "últimos 100 años" — caso contrario, counterparts cuyos
        // docs aparecen en el ranking por DEVENGO al mes pero fueron EMITIDOS
        // en otro período, devuelven page_data vacío y el header queda sin
        // metadata (RUT/tipo/clasificación en blanco). El KPI "Total del mes"
        // viene de get_counterpart_month_docs.total_month, no de aquí.
        const [counterpartRes, docsRes, historyRes] = await Promise.allSettled([
            get_counterpart(resolvedCiId, documentType, null, null, obl),
            get_counterpart_month_docs(resolvedCiId, monthIso, documentType),
            get_counterpart_monthly_history(resolvedCiId, documentType, rangeBounds.from, rangeBounds.to),
        ]);

        if (counterpartRes.status === "fulfilled") {
            const page = counterpartRes.value?.data?.page_data ?? [];
            commit("SET_COUNTERPART", page[0] ?? null);
        } else {
            consoleHandlerError({ error: counterpartRes.reason, prefix: "[clientDetail] counterpart", silent: true });
        }

        if (docsRes.status === "fulfilled") {
            commit("SET_DOCUMENTS", docsRes.value?.data?.documents ?? []);
            commit("SET_TOTAL_MONTH", docsRes.value?.data?.total_month ?? 0);
        } else {
            consoleHandlerError({ error: docsRes.reason, prefix: "[clientDetail] documents", silent: true });
        }

        if (historyRes.status === "fulfilled") {
            commit("SET_MONTHLY_HISTORY", historyRes.value?.data?.series ?? []);
        } else {
            consoleHandlerError({ error: historyRes.reason, prefix: "[clientDetail] history", silent: true });
        }

        const allFailed = [counterpartRes, docsRes, historyRes].every((r) => r.status === "rejected");
        if (allFailed) {
            commit("SET_ERROR", extractErrorMessage(counterpartRes.reason, "Error al cargar el detalle del cliente."));
        }
        commit("SET_LOADING", false);
    },

    /**
     * El usuario cambió el rango del histórico — refetcheamos sólo la serie
     * mensual. Counterpart y documents siguen anclados al mes clickeado.
     */
    async changeRangePeriod({ commit, state }, value) {
        commit("SET_RANGE_PERIOD", value);
        if (!state.ciId || !state.monthIso) return;
        const bounds = computeRangeBoundaries(state.monthIso, value);
        try {
            const res = await get_counterpart_monthly_history(
                state.ciId,
                state.documentType,
                bounds.from,
                bounds.to,
            );
            commit("SET_MONTHLY_HISTORY", res?.data?.series ?? []);
        } catch (error) {
            consoleHandlerError({ error, prefix: "[clientDetail] history-refetch", silent: true });
        }
    },

    /**
     * El detalle se cierra desde fuera (close del drawer o cierre del modal
     * padre). RESET no toca nada más en otros módulos — el caller decide si
     * propaga o no a salesAnalysis.
     */
    closeClientDetail({ commit }) {
        commit("RESET");
    },

    /** Reload tras una clasificación exitosa para reflejar nuevos categorías. */
    async reloadDocuments({ commit, state }) {
        if (!state.ciId || !state.monthIso) return;
        try {
            const res = await get_counterpart_month_docs(
                state.ciId,
                state.monthIso,
                state.documentType,
            );
            commit("SET_DOCUMENTS", res?.data?.documents ?? []);
            commit("SET_TOTAL_MONTH", res?.data?.total_month ?? 0);
        } catch (error) {
            consoleHandlerError({ error, prefix: "[clientDetail] reload-docs", silent: true });
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
